import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCheck, faQrcode } from "@fortawesome/free-solid-svg-icons";
import { colors } from "src/styles";

interface TempplateModalProps {
  isOpen: boolean;
  locationName: string;
  onSelect: (quadrant: number) => void;
  onClose: () => void;
  appLogoSrc: string;
  collapsedLogoSrc?: string;
  primaryColor?: string;
  selectedQuadrant?: number | null;
  disabledQuadrants?: number[];
  onClearAllSelections?: () => void;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: ${colors.white.default};
  border-radius: 5px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 1536px;
  width: 50%;
  max-height: 90vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: ${colors.grey.light1};
  border-bottom: 1px solid ${colors.grey.light3};
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 5px 5px 0 0;

  h2 {
    font-size: 1rem;
    font-weight: bold;
    color: ${colors.grey.dark};
    margin: 0;
  }

  p {
    color: ${colors.grey.dark2};
    margin-top: 0.25rem;
    margin-bottom: 0;
    font-size: 0.875rem;

    span {
      font-weight: 600;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.grey.dark};
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;

  &:hover {
    color: ${colors.black.dark1};
  }

  &:focus {
    outline: none;
  }
`;

const PreviewSection = styled.div`
  padding: 2rem;
  background-color: ${colors.white.default};
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

interface QuadrantCardProps {
  isSelected: boolean;
  isHovered: boolean;
  isDisabled?: boolean;
}

const QuadrantCard = styled.div<QuadrantCardProps>`
  position: relative;
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  border-radius: 0.5rem;
  overflow: hidden;
  opacity: ${(props) => (props.isDisabled ? 0.55 : 1)};
  border: 4px solid
    ${(props) => {
      if (props.isDisabled) return colors.grey.default;
      if (props.isSelected) return colors.kleenway.green;
      if (props.isHovered) return colors.grey.dark2;
      return colors.grey.default;
    }};
  transition: all 0.2s ease;
  box-shadow: ${(props) => {
    if (props.isSelected) return "0 10px 15px -3px rgba(0, 0, 0, 0.1)";
    if (props.isHovered) return "0 20px 25px -5px rgba(0, 0, 0, 0.1)";
    return "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
  }};
  transform: ${(props) => {
    if (props.isDisabled) return "scale(1)";
    if (props.isSelected) return "scale(1.05)";
    if (props.isHovered) return "scale(1.02)";
    return "scale(1)";
  }};
`;

const TemplatePreview = styled.div`
  aspect-ratio: 1;
  background-color: ${colors.white.default};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
`;

const LogoSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0.5rem;

  img {
    height: 1.25rem;
    object-fit: contain;
    margin: 0 0.25rem;
  }
`;

const TitleText = styled.div<{ color: string }>`
  text-align: center;
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  margin-top: 0.25rem;
  color: ${(props) => props.color};
`;

const QRPlaceholder = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 3rem;
    height: 3rem;
  }
`;

const QRPlaceholderBox = styled.div`
  width: 3rem;
  height: 3rem;
  background-color: ${colors.grey.light3};
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${colors.grey.dark4};
  color: ${colors.grey.dark4};
`;

const FooterText = styled.div`
  text-align: center;
  font-size: 0.5rem;
  color: ${colors.grey.dark};
  margin-top: 0.5rem;
  line-height: 1.4;
`;

const QuadrantOverlay = styled.div<{ isHovered: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) => (props.isHovered ? "rgba(32, 138, 253, 0.15)" : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  pointer-events: none;
`;

const QuadrantLabel = styled.div<{ isHovered: boolean }>`
  font-size: 0.875rem;
  font-weight: bold;
  color: ${colors.blue.default};
  background-color: ${colors.white.default};
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  opacity: ${(props) => (props.isHovered ? 1 : 0)};
  transition: opacity 0.2s;
`;

const SelectionCheckmark = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: ${colors.kleenway.green};
  color: ${colors.white.default};
  border-radius: 9999px;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.75rem;
`;

const QuadrantLabelBelow = styled.div`
  text-align: center;
  padding: 0.75rem;
  background-color: ${colors.grey.light7};
  border-top: 1px solid ${colors.grey.light3};

  p {
    margin: 0;
    font-weight: 600;
    color: ${colors.grey.dark};

    &:first-child {
      font-size: 0.875rem;
    }

    &:last-child {
      font-size: 0.75rem;
      color: ${colors.grey.dark2};
    }
  }
`;

const InfoBox = styled.div`
  background-color: ${colors.grey.light7};
  border: 1px solid ${colors.grey.light12};
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1.5rem;

  p {
    font-size: 0.875rem;
    color: ${colors.grey.dark};
    margin: 0;

    span {
      font-weight: 600;
    }
  }
`;

const ModalFooter = styled.div`
  position: sticky;
  bottom: 0;
  background-color: ${colors.white.default};
  border-top: 1px solid ${colors.grey.light3};
  padding: 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const Button = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: ${(props) => (props.variant === "primary" ? "0.5rem 2rem" : "0.5rem 1.5rem")};
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 1rem;

  ${(props) => {
    if (props.variant === "primary") {
      return `
        background-color: ${colors.kleenway.green};
        color: ${colors.white.default};

				&:hover:not(:disabled) {
          background-color: ${colors.green.default};
					box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
				}

				&:disabled {
          background-color: ${colors.grey.default};
          color: ${colors.grey.dark4};
					cursor: not-allowed;
				}
			`;
    }

    return `
      background-color: ${colors.white.default};
      color: ${colors.grey.dark};
      border: 1px solid ${colors.grey.default};

			&:hover {
        background-color: ${colors.grey.light9};
			}
		`;
  }};
`;

export const TemplateQuadrantSelectorModal: React.FC<TempplateModalProps> = ({
  isOpen,
  locationName,
  onSelect,
  onClose,
  appLogoSrc,
  primaryColor = "#7cb342",
  selectedQuadrant = null,
  disabledQuadrants = [],
  onClearAllSelections,
}) => {
  const [localSelectedQuadrant, setLocalSelectedQuadrant] = useState<number | null>(null);
  const [hoveredQuadrant, setHoveredQuadrant] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalSelectedQuadrant(selectedQuadrant);
    }
  }, [isOpen, selectedQuadrant]);

  if (!isOpen) return null;

  const quadrants = [
    { id: 1, position: "top-left", label: "Top Left" },
    { id: 2, position: "top-right", label: "Top Right" },
    { id: 3, position: "bottom-left", label: "Bottom Left" },
    { id: 4, position: "bottom-right", label: "Bottom Right" },
  ];

  const handleSelect = (quadrantId: number) => {
    if (disabledQuadrants.includes(quadrantId) && localSelectedQuadrant !== quadrantId) {
      return;
    }

    setLocalSelectedQuadrant(quadrantId);
  };

  const handleConfirm = () => {
    if (localSelectedQuadrant !== null) {
      onSelect(localSelectedQuadrant);
      onClose();
    }
  };

  const CheckmarkIcon = () => <FontAwesomeIcon icon={faCheck} size="sm" />;

  const QRIcon = () => <FontAwesomeIcon icon={faQrcode} size="2x" style={{ color: "#9ca3af" }} />;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <h2>Select QR Code Position</h2>
            <p>
              Choose which quadrant to display the QR code for <span>{locationName}</span>
            </p>
          </div>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </CloseButton>
        </ModalHeader>

        <PreviewSection>
          <GridContainer>
            {quadrants.map((quadrant) => {
              const isDisabled = disabledQuadrants.includes(quadrant.id) && localSelectedQuadrant !== quadrant.id;

              return (
                <QuadrantCard
                  key={quadrant.id}
                  isSelected={localSelectedQuadrant === quadrant.id}
                  isHovered={hoveredQuadrant === quadrant.id && !isDisabled}
                  isDisabled={isDisabled}
                  onClick={() => handleSelect(quadrant.id)}
                  onMouseEnter={() => setHoveredQuadrant(isDisabled ? null : quadrant.id)}
                  onMouseLeave={() => setHoveredQuadrant(null)}
                >
                  <TemplatePreview>
                    <LogoSection>
                      <img src={appLogoSrc} alt="App Logo" />
                    </LogoSection>

                    <TitleText color={primaryColor}>Time & Attendance</TitleText>

                    <QRPlaceholder>
                      <QRPlaceholderBox>
                        <QRIcon />
                      </QRPlaceholderBox>
                    </QRPlaceholder>

                    <FooterText>
                      <div>For any questions</div>
                      <div>please contact</div>
                      <div>info@ksiintegrated.com</div>
                    </FooterText>

                    <QuadrantOverlay isHovered={hoveredQuadrant === quadrant.id && !isDisabled}>
                      <QuadrantLabel isHovered={hoveredQuadrant === quadrant.id && !isDisabled}>
                        {isDisabled ? "Assigned" : quadrant.label}
                      </QuadrantLabel>
                    </QuadrantOverlay>

                    {localSelectedQuadrant === quadrant.id && (
                      <SelectionCheckmark>
                        <CheckmarkIcon />
                      </SelectionCheckmark>
                    )}
                  </TemplatePreview>

                  <QuadrantLabelBelow>
                    <p>{quadrant.label}</p>
                    <p>Quadrant {quadrant.id}</p>
                  </QuadrantLabelBelow>
                </QuadrantCard>
              );
            })}
          </GridContainer>

          <InfoBox>
            <p>
              <span>💡 Tip:</span> Hover over any quadrant to see a preview highlight. Click to select your preferred
              position for the QR code. You can change your selection at any time.
            </p>
          </InfoBox>
        </PreviewSection>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {onClearAllSelections ? (
            <Button variant="secondary" onClick={onClearAllSelections}>
              Clear All Selections
            </Button>
          ) : null}
          <Button variant="primary" onClick={handleConfirm} disabled={localSelectedQuadrant === null}>
            Generate QR Code
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
};

export default TemplateQuadrantSelectorModal;
